import React, { useEffect, useMemo, useState } from 'react';
import MaterialTable from 'material-table';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';
import moment from 'moment/min/moment-with-locales';
import { ThemeProvider } from '@mui/material/styles';
import { Alert, Box, Chip, Typography } from '@mui/material';
import Grid from '@mui/material/Grid';
import BugReportOutlinedIcon from '@mui/icons-material/BugReportOutlined';
import ReportGmailerrorredOutlinedIcon from '@mui/icons-material/ReportGmailerrorredOutlined';
import ErrorOutlineOutlinedIcon from '@mui/icons-material/ErrorOutlineOutlined';
import GroupOutlinedIcon from '@mui/icons-material/GroupOutlined';
import StorageOutlinedIcon from '@mui/icons-material/StorageOutlined';
import { api } from 'common';
import CircularLoading from '../components/CircularLoading';
import localization from '../components/Table/Localization';
import C2Dialog, { C2DangerButton, C2PrimaryButton, C2SecondaryButton } from '../components/C2Dialog';
import {
  C2,
  C2_FONT,
  C2DocsChip,
  C2EmptyState,
  C2SectionHeading,
  C2Select,
  C2StatusChip,
  C2SummaryCard,
  C2TableCard,
  c2Localization,
  c2TableOptions,
  c2TableStyle,
  c2TableTheme,
  c2Toolbar,
} from '../components/Table/C2UserTable';

const LOG_LIMIT = 1000;
const DAY = 24 * 60 * 60 * 1000;
const RETENTION_DAYS = 30;

const LEVEL_UI = {
  crash: { tone: 'danger', label: 'Cierre', rank: 3 },
  error: { tone: 'warning', label: 'Error', rank: 2 },
  warning: { tone: 'neutral', label: 'Advertencia', rank: 1 },
};

const KIND_UI = {
  render: { label: 'Pantalla con error', hint: 'Una pantalla falló al dibujarse y la app mostró la pantalla de "Algo salió mal". El usuario tuvo que reintentar.' },
  js_fatal: { label: 'Cierre por error de código', hint: 'Un error no controlado del código cerró la app.' },
  js_error: { label: 'Error de código', hint: 'Un error no controlado del código. La app siguió abierta.' },
  promise: { label: 'Operación fallida', hint: 'Una operación en segundo plano falló y nadie manejó el error (por ejemplo, una consulta sin conexión). Normalmente el usuario no lo nota.' },
  unclean_exit: { label: 'Cierre inesperado', hint: 'La app se cerró sola mientras estaba en pantalla. Si aparece "Motivo de Android", ese es el motivo que registró el teléfono; si no aparece (iOS, Android 10 o anterior, o versiones viejas de la app), mira la pantalla, el dispositivo y la versión.' },
  native_crash: { label: 'Cierre por error nativo', hint: 'Falló código nativo de Android (Java/Kotlin o C/C++), por ejemplo el mapa, la cámara o una librería. Si trae traza, la primera línea dice qué falló.' },
  anr: { label: 'App congelada (ANR)', hint: 'La app dejó de responder varios segundos y Android la cerró. Suele ser trabajo pesado en la pantalla (muchos marcadores, listas largas) o una operación que bloqueó la app.' },
  low_memory: { label: 'Cierre por falta de memoria', hint: 'Android cerró la app para liberar memoria. Mira la memoria que usaba la app y el dispositivo: en teléfonos con poca RAM conviene reducir marcadores o imágenes.' },
  app_killed: { label: 'Cerrada por el usuario o el sistema', hint: 'La app se cerró por una acción normal: forzar detención, borrar datos, actualizar o reinstalar la app, o cambiar un permiso. No es un fallo.' },
};

const EXIT_REASON_LABELS = {
  unknown: 'Desconocido',
  exit_self: 'La app terminó su proceso',
  signaled: 'Señal del sistema',
  low_memory: 'Falta de memoria',
  crash: 'Error nativo (Java/Kotlin)',
  crash_native: 'Error nativo (C/C++)',
  anr: 'No respondía (ANR)',
  initialization_failure: 'Falló al iniciar',
  permission_change: 'Cambio de permiso',
  excessive_resource_usage: 'Uso excesivo de recursos',
  user_requested: 'Forzar detención',
  user_stopped: 'Detenida por el usuario',
  dependency_died: 'Murió un proceso del que dependía',
  other: 'Otro',
  freezer: 'Congelada por el sistema',
  package_state_change: 'App deshabilitada o cambiada',
  package_updated: 'App actualizada o reinstalada',
};
const exitReasonLabel = (reason) => EXIT_REASON_LABELS[reason] || reason;

// Los cierres se registran al volver a abrir la app, antes de que se restaure la sesión;
// en esos tipos `preLogin` no significa que el usuario no hubiera iniciado sesión.
const EXIT_KINDS = ['unclean_exit', 'native_crash', 'anr', 'low_memory', 'app_killed'];
const showPreLogin = (l) => !!l.preLogin && !EXIT_KINDS.includes(l.kind);

const LEVEL_FILTERS = [
  { value: 'serious', label: 'Cierres y errores' },
  { value: 'crash', label: 'Solo cierres' },
  { value: 'error', label: 'Solo errores' },
  { value: 'warning', label: 'Advertencias' },
  { value: 'all', label: 'Todo' },
];

const PERIODS = [
  { value: '1', label: 'Últimas 24 h' },
  { value: '7', label: 'Últimos 7 días' },
  { value: '30', label: 'Últimos 30 días' },
  { value: 'all', label: 'Todo' },
];

const PLATFORMS = { android: 'Android', ios: 'iOS' };
const USER_TYPES = { driver: 'Conductor', customer: 'Usuario', admin: 'Admin', fleetadmin: 'Flota' };

const levelUi = (level) => LEVEL_UI[level] || LEVEL_UI.error;
const kindUi = (kind) => KIND_UI[kind] || { label: kind || 'Error', hint: '' };
const versionLabel = (log) => {
  if (!log?.appVersion) return '—';
  return `${log.appVersion}${log.buildNumber ? ` (${log.buildNumber})` : ''}`;
};
const platformLabel = (log) => [PLATFORMS[log?.platform] || log?.platform, log?.osVersion].filter(Boolean).join(' ') || '—';

const pillSx = (active) => ({
  fontFamily: C2_FONT,
  fontWeight: 700,
  borderRadius: '10px',
  border: `1px solid ${active ? C2.violet : C2.border}`,
  color: active ? '#FFFFFF' : C2.dark,
  backgroundColor: active ? C2.violet : '#FFFFFF',
  '&:hover': { backgroundColor: active ? C2.violet : C2.lavender },
});

const codeSx = {
  fontFamily: 'Consolas, "Courier New", monospace',
  fontSize: '0.74rem',
  lineHeight: 1.5,
  color: '#E9E3FF',
  backgroundColor: C2.sidebar,
  borderRadius: '12px',
  p: 1.5,
  m: 0,
  maxHeight: 220,
  overflow: 'auto',
  whiteSpace: 'pre-wrap',
  wordBreak: 'break-word',
};

function InfoRow({ label, children }) {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, py: 0.6, borderBottom: `1px solid ${C2.cellBorder}` }}>
      <Typography sx={{ fontFamily: C2_FONT, fontSize: '0.8rem', color: C2.muted, flexShrink: 0 }}>{label}</Typography>
      <Box sx={{ fontFamily: C2_FONT, fontSize: '0.86rem', fontWeight: 700, color: C2.sidebar, textAlign: 'right', wordBreak: 'break-word' }}>{children}</Box>
    </Box>
  );
}

export default function AppLogs() {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.dir();
  const settings = useSelector((s) => s.settingsdata.settings);
  const users = useSelector((s) => s.usersdata.users);
  const canEdit = !!settings?.AllowCriticalEditsAdmin;

  const [logs, setLogs] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [levelFilter, setLevelFilter] = useState('serious');
  const [platformFilter, setPlatformFilter] = useState('');
  const [period, setPeriod] = useState('7');
  const [view, setView] = useState('grouped');
  const [selectedRow, setSelectedRow] = useState(null);
  const [openFp, setOpenFp] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState(null);

  useEffect(() => {
    const unsubscribe = api.subscribeAppLogs(
      (list) => {
        setLogs(list);
        setLoadError(null);
      },
      (error) => {
        setLogs([]);
        setLoadError(error?.message || String(error));
      },
      LOG_LIMIT
    );
    return () => unsubscribe && unsubscribe();
  }, []);

  const usersById = useMemo(() => {
    const map = {};
    (users || []).forEach((u) => {
      map[u.id] = u;
    });
    return map;
  }, [users]);

  const userName = (uid) => {
    const u = usersById[uid];
    if (!u) return 'Usuario no encontrado';
    return `${u.firstName || ''} ${u.lastName || ''}`.trim() || u.email || u.mobile || 'Sin nombre';
  };

  const userLink = (log) => {
    const type = usersById[log.uid]?.usertype || log.usertype;
    const path = type === 'driver' ? 'driverdetails' : type === 'customer' ? 'customerdetails' : null;
    const label = userName(log.uid);
    if (!path || !usersById[log.uid]) return label;
    return (
      <RouterLink to={`/users/${path}/${log.uid}`} onClick={(e) => e.stopPropagation()} style={{ color: C2.violet }}>
        {label}
      </RouterLink>
    );
  };

  const allLogs = useMemo(() => (logs || []).map((l) => ({ ...l, fingerprint: api.appLogFingerprint(l) })), [logs]);

  const summary = useMemo(() => {
    const now = Date.now();
    const recent = (l, days) => (Number(l.ts) || 0) >= now - days * DAY;
    const affected = new Set(allLogs.filter((l) => recent(l, 7) && l.level !== 'warning').map((l) => l.uid));
    return {
      crashes: allLogs.filter((l) => l.level === 'crash' && recent(l, 1)).length,
      errors: allLogs.filter((l) => l.level === 'error' && recent(l, 1)).length,
      affected: affected.size,
      total: allLogs.length,
    };
  }, [allLogs]);

  const filtered = useMemo(() => {
    const since = period === 'all' ? 0 : Date.now() - Number(period) * DAY;
    return allLogs.filter((l) => {
      if ((Number(l.ts) || 0) < since) return false;
      if (platformFilter && l.platform !== platformFilter) return false;
      if (levelFilter === 'serious') return l.level === 'crash' || l.level === 'error';
      if (levelFilter !== 'all') return l.level === levelFilter;
      return true;
    });
  }, [allLogs, period, platformFilter, levelFilter]);

  const groups = useMemo(() => {
    const map = {};
    filtered.forEach((l) => {
      const g = map[l.fingerprint] || (map[l.fingerprint] = { id: l.fingerprint, fingerprint: l.fingerprint, items: [], users: new Set(), platforms: new Set(), versions: new Set() });
      g.items.push(l);
      g.users.add(l.uid);
      if (l.platform) g.platforms.add(PLATFORMS[l.platform] || l.platform);
      if (l.appVersion) g.versions.add(versionLabel(l));
    });
    return Object.values(map)
      .map((g) => {
        const latest = g.items[0];
        const level = g.items.reduce((best, l) => (levelUi(l.level).rank > levelUi(best).rank ? l.level : best), latest.level);
        return {
          ...g,
          level,
          kind: latest.kind,
          message: latest.message,
          count: g.items.length,
          userCount: g.users.size,
          lastTs: Number(latest.ts) || 0,
          firstTs: Number(g.items[g.items.length - 1].ts) || 0,
          latest,
        };
      })
      .sort((a, b) => levelUi(b.level).rank - levelUi(a.level).rank || b.lastTs - a.lastTs);
  }, [filtered]);

  const openGroup = openFp ? groups.find((g) => g.fingerprint === openFp) || null : null;
  const withStack = openGroup ? openGroup.items.find((l) => l.stack || l.componentStack) : null;

  const deleteGroup = async () => {
    if (!openGroup) return;
    setBusy(true);
    try {
      const n = await api.deleteAppLogs(openGroup.items.map((l) => l.id));
      setNotice({ severity: 'success', msg: `${n} registro${n === 1 ? '' : 's'} borrado${n === 1 ? '' : 's'}.` });
      setOpenFp(null);
      setConfirm(null);
    } catch (e) {
      setNotice({ severity: 'error', msg: `No se pudieron borrar los registros: ${e?.message || e}` });
    } finally {
      setBusy(false);
    }
  };

  const deleteOld = async () => {
    setBusy(true);
    try {
      const n = await api.deleteAppLogsOlderThan(Date.now() - RETENTION_DAYS * DAY);
      setNotice({ severity: 'success', msg: n ? `${n} registro${n === 1 ? '' : 's'} de más de ${RETENTION_DAYS} días borrado${n === 1 ? '' : 's'}.` : `No hay registros de más de ${RETENTION_DAYS} días.` });
      setConfirm(null);
    } catch (e) {
      setNotice({ severity: 'error', msg: `No se pudieron borrar los registros: ${e?.message || e}` });
    } finally {
      setBusy(false);
    }
  };

  const messageCell = (message, kind) => (
    <Box sx={{ textAlign: 'left', minWidth: 220, maxWidth: 460 }}>
      <Typography sx={{ fontFamily: C2_FONT, fontWeight: 700, fontSize: '0.86rem', color: C2.sidebar, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', wordBreak: 'break-word' }}>
        {message || 'Sin mensaje'}
      </Typography>
      <Typography sx={{ fontFamily: C2_FONT, fontSize: '0.74rem', color: C2.muted }}>{kindUi(kind).label}</Typography>
    </Box>
  );

  const dateCell = (ts) => (
    <Box sx={{ textAlign: 'left', whiteSpace: 'nowrap' }}>
      <Typography sx={{ fontFamily: C2_FONT, fontWeight: 700, fontSize: '0.84rem', color: C2.sidebar }}>{ts ? moment(ts).fromNow() : '—'}</Typography>
      <Typography sx={{ fontFamily: C2_FONT, fontSize: '0.72rem', color: C2.muted }}>{ts ? moment(ts).format('lll') : ''}</Typography>
    </Box>
  );

  const groupedColumns = [
    { title: 'Nivel', field: 'level', render: (g) => <C2StatusChip tone={levelUi(g.level).tone} label={levelUi(g.level).label} /> },
    { title: 'Qué pasó', field: 'message', render: (g) => messageCell(g.message, g.kind) },
    { title: 'Veces', field: 'count', type: 'numeric', render: (g) => <Typography sx={{ fontFamily: C2_FONT, fontWeight: 800, color: C2.sidebar }}>{g.count}</Typography> },
    { title: 'Usuarios', field: 'userCount', type: 'numeric' },
    { title: 'Última vez', field: 'lastTs', render: (g) => dateCell(g.lastTs) },
    {
      title: 'Plataforma y versión',
      field: 'platforms',
      sorting: false,
      render: (g) => (
        <Box sx={{ textAlign: 'left' }}>
          <Typography sx={{ fontFamily: C2_FONT, fontSize: '0.82rem', color: C2.sidebar }}>{[...g.platforms].join(', ') || '—'}</Typography>
          <Typography sx={{ fontFamily: C2_FONT, fontSize: '0.72rem', color: C2.muted }}>{[...g.versions].slice(0, 3).join(', ')}</Typography>
        </Box>
      ),
    },
  ];

  const listColumns = [
    { title: 'Fecha', field: 'ts', render: (l) => dateCell(Number(l.ts)) },
    { title: 'Nivel', field: 'level', render: (l) => <C2StatusChip tone={levelUi(l.level).tone} label={levelUi(l.level).label} /> },
    { title: 'Qué pasó', field: 'message', render: (l) => messageCell(l.message, l.kind) },
    {
      title: 'Usuario',
      field: 'uid',
      render: (l) => (
        <Box sx={{ textAlign: 'left' }}>
          <Typography sx={{ fontFamily: C2_FONT, fontWeight: 700, fontSize: '0.84rem', color: C2.sidebar }}>{userLink(l)}</Typography>
          <Typography sx={{ fontFamily: C2_FONT, fontSize: '0.72rem', color: C2.muted }}>{USER_TYPES[usersById[l.uid]?.usertype || l.usertype] || 'Sin tipo'}</Typography>
        </Box>
      ),
    },
    {
      title: 'Dispositivo',
      field: 'device',
      render: (l) => (
        <Box sx={{ textAlign: 'left' }}>
          <Typography sx={{ fontFamily: C2_FONT, fontSize: '0.82rem', color: C2.sidebar }}>{l.device || '—'}</Typography>
          <Typography sx={{ fontFamily: C2_FONT, fontSize: '0.72rem', color: C2.muted }}>{platformLabel(l)} · v{versionLabel(l)}</Typography>
        </Box>
      ),
    },
    { title: 'Pantalla', field: 'screen', render: (l) => <Typography sx={{ fontFamily: C2_FONT, fontSize: '0.82rem', color: C2.sidebar }}>{l.screen || '—'}</Typography> },
  ];

  const toolbar = c2Toolbar(
    <C2Select key="level" id="logs-level" label="Nivel" value={levelFilter} onChange={(e) => setLevelFilter(e.target.value)} minWidth={170} options={LEVEL_FILTERS} />,
    <C2Select key="period" id="logs-period" label="Periodo" value={period} onChange={(e) => setPeriod(e.target.value)} minWidth={160} options={PERIODS} />,
    <C2Select
      key="platform"
      id="logs-platform"
      label="Plataforma"
      value={platformFilter}
      onChange={(e) => setPlatformFilter(e.target.value)}
      minWidth={140}
      options={[{ value: '', label: 'Todas' }, { value: 'android', label: 'Android' }, { value: 'ios', label: 'iOS' }]}
    />,
    canEdit && allLogs.length ? (
      <C2SecondaryButton key="clean" onClick={() => setConfirm('old')}>
        Borrar de más de {RETENTION_DAYS} días
      </C2SecondaryButton>
    ) : null
  );

  if (!logs) return <CircularLoading />;

  const hasFilters = levelFilter !== 'serious' || platformFilter || period !== '7';

  return (
    <Box sx={{ direction: isRTL === 'rtl' ? 'rtl' : 'ltr' }}>
      <ThemeProvider theme={c2TableTheme}>
        {notice ? (
          <Alert severity={notice.severity} onClose={() => setNotice(null)} sx={{ mb: 2, borderRadius: '12px', fontFamily: C2_FONT }}>
            {notice.msg}
          </Alert>
        ) : null}
        {loadError ? (
          <Alert severity="error" sx={{ mb: 2, borderRadius: '12px', fontFamily: C2_FONT }}>
            No se pudieron cargar los logs: {loadError}
          </Alert>
        ) : null}

        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} sm={6} lg={3}>
            <C2SummaryCard icon={ReportGmailerrorredOutlinedIcon} label="Cierres (24 h)" value={summary.crashes} caption="La app se cerró o mostró la pantalla de error" accent={summary.crashes ? '#DC2626' : C2.violet} />
          </Grid>
          <Grid item xs={12} sm={6} lg={3}>
            <C2SummaryCard icon={ErrorOutlineOutlinedIcon} label="Errores (24 h)" value={summary.errors} caption="Errores de código sin cierre" accent={summary.errors ? '#D97706' : C2.violet} />
          </Grid>
          <Grid item xs={12} sm={6} lg={3}>
            <C2SummaryCard icon={GroupOutlinedIcon} label="Usuarios afectados" value={summary.affected} caption="Con cierres o errores en 7 días" />
          </Grid>
          <Grid item xs={12} sm={6} lg={3}>
            <C2SummaryCard icon={StorageOutlinedIcon} label="Registros" value={summary.total} caption={`Se cargan los últimos ${LOG_LIMIT}`} />
          </Grid>
        </Grid>

        <C2TableCard>
          <C2SectionHeading
            title="Cierres y errores de la app"
            subtitle="La app de conductores y usuarios reporta aquí cada cierre y error, con el dispositivo, la versión y la pantalla donde pasó. Los errores iguales se agrupan."
          />
          <Box sx={{ display: 'flex', gap: 1, mb: 1.5, flexWrap: 'wrap' }}>
            <Chip label={`Agrupados (${groups.length})`} onClick={() => setView('grouped')} sx={pillSx(view === 'grouped')} />
            <Chip label={`Uno por uno (${filtered.length})`} onClick={() => setView('list')} sx={pillSx(view === 'list')} />
          </Box>
          <MaterialTable
            title="Logs"
            columns={view === 'grouped' ? groupedColumns : listColumns}
            data={view === 'grouped' ? groups : filtered}
            style={c2TableStyle(isRTL)}
            components={toolbar}
            onRowClick={(evt, row) => {
              setSelectedRow(row.tableData.id);
              setOpenFp(row.fingerprint);
            }}
            options={{
              ...c2TableOptions(selectedRow),
              actionsColumnIndex: -1,
              pageSize: 10,
              pageSizeOptions: [10, 20, 50],
              rowStyle: (row) => ({
                backgroundColor: row.level === 'crash' ? '#FEF2F2' : selectedRow === row.tableData.id ? C2.lavender : '#FFFFFF',
                fontFamily: C2_FONT,
              }),
            }}
            localization={c2Localization(
              localization(t),
              <C2EmptyState
                icon={BugReportOutlinedIcon}
                title={hasFilters ? 'No hay registros con estos filtros' : 'Sin cierres ni errores en los últimos 7 días'}
                message="Cuando la app de un conductor o usuario se cierre o tenga un error, aparecerá aquí. Los reportes llegan cuando la persona vuelve a abrir la app con sesión iniciada."
              />
            )}
            actions={[
              {
                icon: () => <C2DocsChip icon={BugReportOutlinedIcon} label="Ver" />,
                tooltip: 'Ver detalle',
                onClick: (e, row) => setOpenFp(row.fingerprint),
              },
            ]}
          />
        </C2TableCard>

        <C2Dialog
          open={!!openGroup}
          onClose={() => {
            if (busy) return;
            setOpenFp(null);
            setConfirm(null);
          }}
          title={openGroup ? kindUi(openGroup.kind).label : 'Detalle'}
          icon={BugReportOutlinedIcon}
          maxWidth={820}
          actions={
            confirm === 'group' ? (
              <>
                <Typography sx={{ fontFamily: C2_FONT, fontSize: '0.84rem', color: '#B91C1C', mr: 'auto' }}>
                  ¿Borrar los {openGroup?.count} registros de este error?
                </Typography>
                <C2SecondaryButton onClick={() => setConfirm(null)} disabled={busy}>
                  Cancelar
                </C2SecondaryButton>
                <C2DangerButton onClick={deleteGroup} disabled={busy}>
                  {busy ? 'Borrando…' : 'Sí, borrar'}
                </C2DangerButton>
              </>
            ) : (
              <>
                {canEdit ? (
                  <C2DangerButton onClick={() => setConfirm('group')} sx={{ mr: 'auto' }}>
                    Borrar registros
                  </C2DangerButton>
                ) : null}
                <C2PrimaryButton onClick={() => setOpenFp(null)}>Cerrar</C2PrimaryButton>
              </>
            )
          }
        >
          {openGroup ? (
            <Box>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1.5 }}>
                <C2StatusChip tone={levelUi(openGroup.level).tone} label={levelUi(openGroup.level).label} />
                <C2StatusChip tone="violet" dot={false} label={`${openGroup.count} ${openGroup.count === 1 ? 'vez' : 'veces'}`} />
                <C2StatusChip tone="violet" dot={false} label={`${openGroup.userCount} usuario${openGroup.userCount === 1 ? '' : 's'}`} />
              </Box>
              <Box sx={{ p: 1.5, borderRadius: '12px', backgroundColor: C2.lavender, border: `1px solid ${C2.border}`, mb: 1.5 }}>
                <Typography sx={{ fontFamily: C2_FONT, fontWeight: 700, fontSize: '0.92rem', color: C2.sidebar, wordBreak: 'break-word' }}>{openGroup.message || 'Sin mensaje'}</Typography>
                {kindUi(openGroup.kind).hint ? (
                  <Typography sx={{ fontFamily: C2_FONT, fontSize: '0.8rem', color: C2.muted, mt: 0.5 }}>{kindUi(openGroup.kind).hint}</Typography>
                ) : null}
              </Box>

              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <InfoRow label="Primera vez">{moment(openGroup.firstTs).format('lll')}</InfoRow>
                  <InfoRow label="Última vez">{`${moment(openGroup.lastTs).format('lll')} (${moment(openGroup.lastTs).fromNow()})`}</InfoRow>
                  <InfoRow label="Plataformas">{[...openGroup.platforms].join(', ') || '—'}</InfoRow>
                  <InfoRow label="Versiones">{[...openGroup.versions].join(', ') || '—'}</InfoRow>
                  <InfoRow label="Pantallas">{[...new Set(openGroup.items.map((l) => l.screen).filter(Boolean))].slice(0, 5).join(', ') || '—'}</InfoRow>
                  {openGroup.items.some((l) => l.exitReason) ? (
                    <InfoRow label="Motivo de Android">
                      {[...new Set(openGroup.items.map((l) => l.exitReason).filter(Boolean))].map(exitReasonLabel).join(', ')}
                    </InfoRow>
                  ) : null}
                  {openGroup.items.find((l) => l.memoryMb) ? (
                    <InfoRow label="Memoria usada por la app">{`${openGroup.items.find((l) => l.memoryMb).memoryMb} MB`}</InfoRow>
                  ) : null}
                  {openGroup.items.find((l) => l.exitDetail) ? (
                    <InfoRow label="Detalle del sistema">{openGroup.items.find((l) => l.exitDetail).exitDetail}</InfoRow>
                  ) : null}
                </Grid>
                <Grid item xs={12} md={6}>
                  {withStack?.stack ? (
                    <>
                      <Typography sx={{ fontFamily: C2_FONT, fontWeight: 700, fontSize: '0.8rem', color: C2.sidebar, mb: 0.5 }}>Traza del error</Typography>
                      <Box component="pre" sx={codeSx}>{withStack.stack}</Box>
                    </>
                  ) : null}
                  {withStack?.componentStack ? (
                    <>
                      <Typography sx={{ fontFamily: C2_FONT, fontWeight: 700, fontSize: '0.8rem', color: C2.sidebar, mt: 1, mb: 0.5 }}>Componentes involucrados</Typography>
                      <Box component="pre" sx={codeSx}>{withStack.componentStack.trim()}</Box>
                    </>
                  ) : null}
                  {!withStack ? (
                    <Typography sx={{ fontFamily: C2_FONT, fontSize: '0.82rem', color: C2.muted }}>Este tipo de registro no trae traza técnica.</Typography>
                  ) : null}
                </Grid>
              </Grid>

              <Typography sx={{ fontFamily: C2_FONT, fontWeight: 700, fontSize: '0.86rem', color: C2.sidebar, mt: 2, mb: 0.75 }}>
                Ocurrencias {openGroup.count > 20 ? '(las 20 más recientes)' : ''}
              </Typography>
              <Box sx={{ border: `1px solid ${C2.border}`, borderRadius: '12px', overflow: 'hidden' }}>
                {openGroup.items.slice(0, 20).map((l) => (
                  <Box key={l.id} sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1.1fr 1.2fr 1.4fr 1fr' }, gap: 1, px: 1.5, py: 1, borderBottom: `1px solid ${C2.cellBorder}`, '&:last-of-type': { borderBottom: 'none' } }}>
                    <Typography sx={{ fontFamily: C2_FONT, fontSize: '0.8rem', color: C2.sidebar }}>{moment(Number(l.ts)).format('lll')}</Typography>
                    <Typography sx={{ fontFamily: C2_FONT, fontSize: '0.8rem', color: C2.sidebar, fontWeight: 700 }}>
                      {userLink(l)}
                      {showPreLogin(l) ? <Box component="span" sx={{ color: C2.muted, fontWeight: 400 }}> · antes de iniciar sesión</Box> : null}
                    </Typography>
                    <Typography sx={{ fontFamily: C2_FONT, fontSize: '0.8rem', color: C2.muted }}>
                      {[l.device, platformLabel(l), `v${versionLabel(l)}`].filter(Boolean).join(' · ')}
                    </Typography>
                    <Typography sx={{ fontFamily: C2_FONT, fontSize: '0.8rem', color: C2.muted }}>{l.screen || 'Pantalla desconocida'}</Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          ) : null}
        </C2Dialog>

        <C2Dialog
          open={confirm === 'old'}
          onClose={() => (busy ? null : setConfirm(null))}
          title="Borrar registros antiguos"
          icon={BugReportOutlinedIcon}
          actions={
            <>
              <C2SecondaryButton onClick={() => setConfirm(null)} disabled={busy}>
                Cancelar
              </C2SecondaryButton>
              <C2DangerButton onClick={deleteOld} disabled={busy}>
                {busy ? 'Borrando…' : 'Borrar'}
              </C2DangerButton>
            </>
          }
        >
          <Typography sx={{ fontFamily: C2_FONT, fontSize: '0.9rem', color: C2.sidebar }}>
            Se borrarán todos los registros de hace más de {RETENTION_DAYS} días. Esta acción no se puede deshacer.
          </Typography>
        </C2Dialog>
      </ThemeProvider>
    </Box>
  );
}
